import { Box } from '@mui/material';
import React, { forwardRef, useCallback } from 'react';

import { PER_PAGE_OPTIONS } from '../../constants/table';
import useClearFiltersCallback from '../../hooks/useClearFiltersCallback';
import { IFiltersRef } from '../../types/tables';
import { IClaimLinkTemplate, ITemplateCardConfig } from '../../types/templates';
import { thinScrollbarStyles } from '../../utils/ui';
import PaginatedList, { IPaginatedListRef } from '../PaginatedList/PaginatedList';
import {
  PageContent,
  PaginationControls,
  TableEmptyPlaceholder,
  TableFilteredEmptyPlaceholder,
  TableViewLoader,
} from '../_mui';
import { ClaimLinkTemplateCard } from './index';

interface IProps {
  templates: IClaimLinkTemplate[];
  isLoading?: boolean;
  filtersRef?: React.RefObject<IFiltersRef>;
  totalCount: number;
  pageSize: number;
  page?: number;
  onChange: (event: { page: number; setPage: (p: number) => void }) => void;
  onPageSizeChange: (newPageSize: number) => void;
  hasFiltersApplied?: boolean;
  cardConfig?: ITemplateCardConfig;
  disableItemsPerPage?: boolean;
  isModal?: boolean;
}

export const ClaimLinkTemplatesList = forwardRef<IPaginatedListRef<IClaimLinkTemplate>, IProps>(
  (
    {
      templates,
      isLoading = false,
      filtersRef,
      totalCount,
      pageSize,
      page,
      onChange,
      onPageSizeChange,
      hasFiltersApplied,
      cardConfig,
      disableItemsPerPage = false,
      isModal = false,
    },
    ref,
  ) => {
    const handleClearFilters = useClearFiltersCallback(filtersRef ?? null);

    const renderContent = useCallback(
      (templatesList: IClaimLinkTemplate[]) => {
        const noFilteredItems = !templatesList || !templatesList.length;
        const noTemplatesAtAll = totalCount === 0 && noFilteredItems && !hasFiltersApplied;

        if (noTemplatesAtAll) {
          // In the picker (modal) there's no inline create action, so guide the user to
          // Experience Manager; on the management page a shorter prompt is enough.
          if (isModal) {
            return (
              <TableEmptyPlaceholder
                item="claim link templates"
                title="No claim link templates yet."
                subtitle="Save your changes, then go to Experience Manager → Claim Link Templates to create one. Come back here to select it once it's ready."
              />
            );
          }

          return (
            <TableEmptyPlaceholder
              item="claim link templates"
              subtitle="Create a template to reuse layout, branding, and content across sends"
            />
          );
        }

        if (noFilteredItems) {
          return <TableFilteredEmptyPlaceholder itemType="claim link templates" onClearFilters={handleClearFilters} />;
        }

        if (templatesList.length > 0) {
          const listStyles = {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: 3,
            width: '100%',
            alignItems: 'stretch',
            alignSelf: 'start',
          };

          return (
            <PageContent>
              <Box sx={listStyles}>
                {templatesList.map((template) => (
                  <ClaimLinkTemplateCard key={template.uid} template={template} config={cardConfig} />
                ))}
              </Box>
            </PageContent>
          );
        }
      },
      [totalCount, hasFiltersApplied, handleClearFilters, cardConfig, isModal],
    );

    return (
      <PaginatedList<IClaimLinkTemplate>
        ref={ref}
        items={templates}
        pageSize={pageSize}
        totalCount={totalCount}
        onChange={onChange}
        isFinalItems={false}
        defaultValue={page}
      >
        {({ items: templatesList, pagination: Pagination }) => {
          const visibleTemplates = !isLoading && templatesList.length > 0;

          const content = isLoading ? <TableViewLoader height="100%" /> : renderContent(templatesList);

          const pagination = visibleTemplates && (
            <PaginationControls
              pageSize={pageSize}
              options={PER_PAGE_OPTIONS}
              onPageSizeChange={onPageSizeChange}
              PaginationComponent={Pagination}
              disableItemsPerPage={disableItemsPerPage}
            />
          );
          if (isModal) {
            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    ...thinScrollbarStyles,
                  }}
                >
                  {content}
                </Box>
                {pagination && <Box sx={{ flexShrink: 0, backgroundColor: 'background.paper' }}>{pagination}</Box>}
              </Box>
            );
          }

          return (
            <>
              {content}
              {pagination}
            </>
          );
        }}
      </PaginatedList>
    );
  },
);

ClaimLinkTemplatesList.displayName = 'ClaimLinkTemplatesList';
